import React from 'react';
import PropTypes from 'prop-types';

export default function ThreadList({
  threads,
  loading,
  activeThreadId,
  onSelect,
  userProfiles,
  currentUserId,
  supportAdminId,
  supportAdminIds = [],
  unreadCountsByThread = {},
  filter = 'inbox',
  onFilterChange,
  onNewThread,
  teamNamesById = {},
  isMobile,
  onHide
}) {
  const supportIds = new Set([
    ...supportAdminIds,
    supportAdminId,
  ].filter(Boolean));

  const getUserName = (uid) => {
    if (!uid) return 'Unknown User';

    const profile = userProfiles?.[uid];
    const { firstName = '', lastName = '' } = profile || {};
    const name = `${firstName} ${lastName}`.trim() || 'Unnamed';

    // If it's a support chat, append label instead of replacing name
    if (supportIds.has(uid)) {
      return `${name} (Support)`;
    }

    return name;
  };

  const handleSelect = (threadId) => {
    onSelect(threadId);
    if (isMobile && onHide) {
      onHide(); // hide sidebar on mobile
    }
  };

  return (
    <div className="messages-sidebar">
      <div className="sidebar-header-row">
        <div className="sidebar-header-row__top">
          <h3 className="sidebar-header">Conversations</h3>
          <button type="button" className="thread-new-message-button" onClick={onNewThread}>
            New
          </button>
        </div>
        <div className="thread-filter-toggle" role="tablist" aria-label="Conversation filter">
          <button
            type="button"
            className={filter === 'inbox' ? 'active' : ''}
            onClick={() => onFilterChange?.('inbox')}
          >
            Inbox
          </button>
          <button
            type="button"
            className={filter === 'archived' ? 'active' : ''}
            onClick={() => onFilterChange?.('archived')}
          >
            Archived
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-muted">Loading conversations…</p>
      ) : threads.length === 0 ? (
        <div style={{ color: '#666', fontSize: '0.9rem' }}>
          {filter === 'archived' ? (
            <p>No archived conversations.</p>
          ) : (
            <>
              <p>You don’t have any messages yet.</p>
              <p>Direct messages start from a club’s admin or member list.</p>
              <p>Inbox broadcasts from club admins will also appear here.</p>
            </>
          )}
        </div>
      ) : (
        <ul className="thread-list">
          {threads.map((thread) => {
            const otherId = thread.participants.find(uid => uid !== currentUserId);
            const isActive = thread.id === activeThreadId;
            const unreadCount = Number(unreadCountsByThread?.[thread.id] || 0);
            const teamName = thread.teamId ? teamNamesById[thread.teamId] || 'Club conversation' : '';

            return (
              <li
                key={thread.id}
                className={`thread-list-item ${isActive ? 'active' : ''}`}
                onClick={() => handleSelect(thread.id)}
                style={{
                  cursor: 'pointer',
                  padding: '8px',
                  borderBottom: '1px solid #eee'
                }}
              >
                <div className="thread-list-item__main">
                  <div className="thread-list-item__topline">
                    <span className="thread-list-item__title">
                      <strong>{getUserName(otherId)}</strong>
                      {thread.lastMessageIsBroadcast && (
                        <span className="chat-thread-broadcast-pill">Broadcast</span>
                      )}
                    </span>
                  </div>
                  {thread.lastMessage && (
                    <p className="last-message">{thread.lastMessage}</p>
                  )}
                  {teamName && (
                    <p className="thread-list-item__team">{teamName}</p>
                  )}
                </div>
                {unreadCount > 0 && (
                  <span className="message-unread-badge thread-list-item__badge" aria-label={`${unreadCount} unread in this conversation`}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

ThreadList.propTypes = {
  threads: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      participants: PropTypes.arrayOf(PropTypes.string).isRequired,
      lastMessage: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  activeThreadId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  userProfiles: PropTypes.objectOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    })
  ).isRequired,
  currentUserId: PropTypes.string.isRequired,
  supportAdminId: PropTypes.string,
  supportAdminIds: PropTypes.arrayOf(PropTypes.string),
  unreadCountsByThread: PropTypes.objectOf(PropTypes.number),
  filter: PropTypes.oneOf(['inbox', 'archived']),
  onFilterChange: PropTypes.func,
  onNewThread: PropTypes.func,
  teamNamesById: PropTypes.objectOf(PropTypes.string),
  isMobile: PropTypes.bool,
  onHide: PropTypes.func
};
